import React from "react";
import { observer } from "mobx-react";
import { useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import $apiKeys from "@/core/apiKeys/store";
import $user from "@/core/user/store";
import CreateKeyDialog from "./CreateKeyDialog";
import KeysTable from "./KeysTable";
import McpGuide from "./McpGuide";
import RegisterDialog from "./RegisterDialog";
import SharedAccountNotice from "./SharedAccountNotice";
import TokenDialog from "./TokenDialog";

// Порядок блоков повторяет сценарий: выпустил ключ → подключил агента → следишь
// за списком. Форма выпуска живёт в диалоге, а не в потоке страницы: токен
// показывается один раз, и этот момент должен быть осознанным действием.
const ApiKeys = () => {
  const [token, setToken] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const [registering, setRegistering] = React.useState(false);
  // Стартовое значение — «свёрнуто»: список ключей ещё не загружен, и раскрытая
  // по умолчанию инструкция схлопывалась бы анимацией у всех, у кого ключи есть.
  const [guideOpen, setGuideOpen] = React.useState(false);
  const { hash } = useLocation();

  const isAdmin = $user.role === "admin";

  // Пришли по кнопке «Подробнее». Флаг нужен эффекту загрузки: тот решает про
  // инструкцию уже после await, то есть заведомо позже эффекта хеша, и без
  // флага схлопнул бы её у того, кто явно за ней и пришёл.
  const openedByHash = React.useRef(false);

  React.useEffect(() => {
    // Инструкция раскрывается, только если ключей нет: пришедший за первым
    // ключом должен увидеть шаги сразу. Решение принимается один раз, после
    // загрузки списка — дальше состоянием распоряжается пользователь.
    (async () => {
      await $apiKeys.fetchData();
      if (openedByHash.current) return;

      setGuideOpen($apiKeys.getData.length === 0);
    })();
  }, []);

  React.useEffect(() => {
    if (hash !== "#mcp") return;

    // Одного скролла мало — к свёрнутой инструкции прокручивать бессмысленно.
    openedByHash.current = true;
    setGuideOpen(true);
    document.getElementById("mcp")?.scrollIntoView({ behavior: "smooth" });
  }, [hash]);

  const onRegistered = () => {
    setRegistering(false);
    // Сессия сменилась: у новой учётки свой список ключей.
    $apiKeys.fetchData();
  };

  return (
    <Box sx={{ m: 2, maxWidth: 900 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6">API-ключи</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
          Ключ даёт агенту в редакторе кода доступ к переводам через MCP. Он
          работает с вашими правами и показывается ровно один раз при выпуске.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ mt: 2 }}
          onClick={() => setCreating(true)}
        >
          Выпустить ключ
        </Button>
      </Box>

      {$user.shared && (
        <SharedAccountNotice onRegister={() => setRegistering(true)} />
      )}

      <McpGuide expanded={guideOpen} onToggle={setGuideOpen} />

      <KeysTable isAdmin={isAdmin} onCreate={() => setCreating(true)} />

      <CreateKeyDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(issued) => {
          setCreating(false);
          setToken(issued);
        }}
      />
      <TokenDialog token={token} onClose={() => setToken(null)} />
      <RegisterDialog
        open={registering}
        onClose={() => setRegistering(false)}
        onCreated={onRegistered}
      />
    </Box>
  );
};

export default observer(ApiKeys);
